import React, { lazy, Suspense, useCallback, useState } from "react";
import { ConfigureVaultModal } from "@/components/ConfigureVaultModal";
import { useAction } from "@/components/toolkit/InputProvider";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { container } from "@/utils/mobx";
import { Split, SplitPane } from "@/components/toolkit/Split";
import { Accordion } from "@/components/toolkit/Accordion";
import { Tabs } from "@/components/toolkit/Tabs";
import { isTouchDevice } from "@/utils/device";
import { ConfirmDialog } from "@/components/toolkit/ConfirmDialog";
import { TextInput } from "@/components/toolkit/TextInput";
import { Clickable } from "@/components/toolkit/Clickable";
import { ContextMenu } from "@/components/toolkit/ContextMenu";
import { NavigationBar } from "@/components/toolkit/NavigationBar";
import { BranchSelect } from "@/components/BranchSelect";
import { FileAction, ReviewSectionStore } from "./store";
import { appStore } from "@/app_store";
import { t } from "@/i18n";
import "./s.review_section.css";

const MonacoEditor = lazy(() => import("@/components/toolkit/MonacoEditor").then(m => ({ default: m.MonacoEditor })));

function labelToDisplay(label: string): string {
    switch (label) {
        case "index_new":      return "added";
        case "index_modified": return "modified";
        case "index_deleted":  return "deleted";
        case "index_renamed":  return "renamed";
        case "wt_modified":    return "modified";
        case "wt_deleted":     return "deleted";
        case "wt_renamed":     return "renamed";
        case "wt_new":         return "untracked";
        case "conflicted":     return "conflict";
        default:               return label;
    }
}

function primaryLabel(labels: string[]): string {
    const first = labels[0];
    return first ? labelToDisplay(first) : "";
}

// Row actions; while a request for this path is in flight the clicked action becomes
// a preloader and the other actions are hidden (slot kept so nothing shifts) until it resolves.
const FileActions = observer((props: { store: ReviewSectionStore; path: string; primary: "stage" | "unstage" }) => {
    const { store, path, primary } = props;
    const pending: FileAction | undefined = store.pendingFileActions.get(path);
    const spinner = (
        <span className="review_section__file_action review_section__file_action--pending">
            <span className="review_section__file_action_spinner" />
        </span>
    );
    const primaryBtn = (
        <span
            className={"review_section__file_action" + (pending ? " review_section__file_action--hidden" : "")}
            title={primary === "stage" ? "Stage" : "Unstage"}
        >
            {primary === "stage" ? "+" : "−"}
        </span>
    );
    const revertBtn = (
        <span
            className={"review_section__file_action review_section__file_action--remove" + (pending ? " review_section__file_action--hidden" : "")}
            title={t("review_ctx_revert")}
        >
            ↶
        </span>
    );
    if (pending) {
        return (
            <div className="review_section__file_actions review_section__file_actions--pending">
                {pending === "revert" ? primaryBtn : spinner}
                {pending === "revert" ? spinner : revertBtn}
            </div>
        );
    }
    return (
        <div className="review_section__file_actions">
            <Clickable stopPropagation onClick={() => primary === "stage" ? store.stageFile(path) : store.unstageFile(path)}>
                {primaryBtn}
            </Clickable>
            <Clickable stopPropagation onClick={() => store.promptRemoveFile(path)}>
                {revertBtn}
            </Clickable>
        </div>
    );
});

export const ReviewSection = container((props: {
    store: ReviewSectionStore;
}) => {
    const store = props.store;
    const { loadState } = store;

    const [wordWrap, setWordWrap] = useState(false);
    const [showVaultModal, setShowVaultModal] = useState(false);

    const showFileContextMenu = useCallback((path: string, kind: "staged" | "unstaged" | "untracked" | "conflict", x: number, y: number) => {
        const items = [];
        // Conflicted files can't be staged until all markers are resolved.
        if (kind === "staged") items.push({ title: t("review_ctx_unstage"), value: "unstage" });
        else if (kind !== "conflict") items.push({ title: t("review_ctx_stage"), value: "stage" });
        items.push({ title: t("review_ctx_revert"), value: "revert" });
        store.contextMenuStore.show(x, y, items, (value: string) => {
            if (value === "stage") store.stageFile(path);
            else if (value === "unstage") store.unstageFile(path);
            else if (value === "revert") store.revertFile(path);
        });
    }, [store]);

    useAction('reviewsection', 'close-tab',        () => { if (store.activeTabPath) store.closeTab(store.activeTabPath); }, [store]);
    useAction('reviewsection', 'prev-tab',         () => store.navigateTab(-1), [store]);
    useAction('reviewsection', 'next-tab',         () => store.navigateTab(1), [store]);
    useAction('reviewsection', 'move-tab-left',    () => store.moveActiveTab(-1), [store]);
    useAction('reviewsection', 'move-tab-right',   () => store.moveActiveTab(1), [store]);
    useAction('reviewsection', 'close-other-tabs', () => store.closeAllTabsExceptActive(), [store]);
    useAction('reviewsection', 'save-file',        () => { if (store.activeEditable) store.saveActive(); }, [store]);

    if (loadState === "agent-offline") {
        return (
            <div className="review_section review_section--centered" data-input-component="reviewsection" data-input-focus="click">
                <div className="review_section__empty_icon">⚡</div>
                <div className="review_section__empty_title">{t("review_agent_offline")}</div>
                <div className="review_section__empty_desc">{t("review_agent_offline_desc")}</div>
                <Clickable onClick={() => store.retry()}>
                    <button className="review_section__action_btn">{t("review_retry")}</button>
                </Clickable>
            </div>
        );
    }

    if (loadState === "no-git") {
        return (
            <div className="review_section review_section--centered" data-input-component="reviewsection" data-input-focus="click">
                <div className="review_section__empty_icon">⎇</div>
                <div className="review_section__empty_title">{t("review_no_git")}</div>
                <div className="review_section__empty_desc">
                    {store.projectName
                        ? t("review_no_git_desc").replace("{name}", store.projectName)
                        : t("review_no_git_desc_generic")}
                </div>
            </div>
        );
    }

    // loadState === "idle" | "loading" | "loaded"
    const isLoading = loadState === "idle" || loadState === "loading";

    const branchSelect = <BranchSelect />;

    const leftPane = (
        <div className="review_section__pane review_section__pane--sidebar">
            {!store.isMobile && (
                <div className="review_section__pane_header">
                    {branchSelect}
                </div>
            )}

            {store.operationError && (
                <div className="review_section__error_banner" role="alert">
                    <span className="review_section__error_banner_icon">⚠</span>
                    <div className="review_section__error_banner_msg">{store.operationError}</div>
                    <Clickable stopPropagation onClick={() => store.dismissError()}>
                        <span className="review_section__error_banner_dismiss" title={t("review_dismiss_error")}>×</span>
                    </Clickable>
                </div>
            )}

            {store.canAbort && (
                <div className="review_section__error_banner" role="alert">
                    <span className="review_section__error_banner_icon">⚠</span>
                    <div className="review_section__error_banner_msg">
                        <div>{t("review_merge_in_progress_banner")}</div>
                        {store.pendingStashMerge && (
                            <div className="review_section__error_banner_hint">{t("review_pending_stash_merge")}</div>
                        )}
                        <Clickable onClick={() => store.abortMerge()}>
                            <button className="review_section__merge_btn" disabled={store.isRepoBusy}>
                                {store.isAborting ? t("review_aborting") : t("review_abort_merge")}
                            </button>
                        </Clickable>
                    </div>
                </div>
            )}

            {store.mergeRequired && (
                <div className="review_section__error_banner" role="alert">
                    <span className="review_section__error_banner_icon">⚠</span>
                    <div className="review_section__error_banner_msg">
                        <div className="review_section__error_banner_title">{t("review_merge_required")}</div>
                        <div>{t("review_merge_required_desc")}</div>
                        <Clickable onClick={() => store.startMerge()}>
                            <button className="review_section__merge_btn" disabled={store.isRepoBusy}>
                                {store.isPulling ? t("review_merging") : t("review_start_merge")}
                            </button>
                        </Clickable>
                    </div>
                    <Clickable stopPropagation onClick={() => store.dismissMergeRequired()}>
                        <span className="review_section__error_banner_dismiss" title={t("review_dismiss_error")}>×</span>
                    </Clickable>
                </div>
            )}

            {store.identityRequired && (
                <div className="review_section__error_banner" role="alert">
                    <span className="review_section__error_banner_icon">⚠</span>
                    <div className="review_section__error_banner_msg">
                        <div className="review_section__error_banner_title">{t("review_identity_required")}</div>
                        <div>{t("review_identity_required_desc")}</div>
                        <div className="review_section__identity_form">
                            <TextInput
                                className="review_section__identity_input"
                                placeholder={t("review_identity_name")}
                                value={store.identityName}
                                onChange={v => { store.identityName = v; }}
                            />
                            <TextInput
                                className="review_section__identity_input"
                                placeholder={t("review_identity_email")}
                                value={store.identityEmail}
                                onChange={v => { store.identityEmail = v; }}
                                onEnter={() => store.saveIdentity()}
                            />
                            <Clickable onClick={() => store.saveIdentity()}>
                                <button
                                    className="review_section__merge_btn"
                                    disabled={store.isSavingIdentity || !store.identityName.trim() || !store.identityEmail.trim()}
                                >
                                    {store.isSavingIdentity ? t("review_saving_identity") : t("review_save_identity")}
                                </button>
                            </Clickable>
                        </div>
                    </div>
                    <Clickable stopPropagation onClick={() => store.dismissIdentity()}>
                        <span className="review_section__error_banner_dismiss" title={t("review_dismiss_error")}>×</span>
                    </Clickable>
                </div>
            )}

            {isLoading ? (
                <div className="review_section review_section--centered">
                    <div className="review_section__spinner" />
                </div>
            ) : <>

            {store.isMobile && store.isLandscape && (
                <div className="branch_select_row">
                    {branchSelect}
                </div>
            )}

            {store.canCommit && (
                <div className="review_section__commit_area">
                    {store.isMerging && (
                        <div className="review_section__merge_hint">{t("review_merge_in_progress")}</div>
                    )}
                    <TextInput
                        isTextArea
                        className="review_section__commit_input"
                        placeholder={t("review_commit_placeholder")}
                        value={store.commitMessage}
                        onChange={v => { store.commitMessage = v; }}
                        onEnter={() => store.commit()}
                    />
                    {store.commitError && (
                        <div className="review_section__commit_error">{store.commitError}</div>
                    )}
                    <Clickable onClick={() => store.commit()}>
                        <button
                            className="review_section__commit_btn"
                            disabled={store.isCommitting || (!store.commitMessage.trim() && !store.isMerging)}
                        >
                            {store.isCommitting ? t("review_committing") : (store.isMerging ? t("review_commit_merge_btn") : t("review_commit_btn"))}
                        </button>
                    </Clickable>
                </div>
            )}

            {store.totalChanges === 0 && !store.canCommit ? (
                <div className="review_section__no_changes">{t("review_no_changes")}</div>
            ) : (
                <div className="review_section__file_list">
                    {store.conflictEntries.length > 0 && (
                        <div className="review_section__group">
                            <div className="review_section__group_header">
                                <span className="review_section__group_label">{t("review_conflicts")}</span>
                            </div>
                            {store.conflictEntries.map(entry => (
                                <Clickable key={entry.path} onClick={() => store.selectFile(entry.path)}>
                                    <button
                                        className={"review_section__file" + (store.selectedFilePath === entry.path ? " active" : "")}
                                        onContextMenu={(e) => { e.preventDefault(); showFileContextMenu(entry.path, "conflict", e.clientX, e.clientY); }}
                                    >
                                        <span className="review_section__file_status review_section__file_status--conflicted">
                                            {t("review_conflict_label")}
                                        </span>
                                        <span className="review_section__file_path">{entry.path}</span>
                                    </button>
                                </Clickable>
                            ))}
                        </div>
                    )}

                    {store.resolvedConflictEntries.length > 0 && (
                        <div className="review_section__group">
                            <div className="review_section__group_header">
                                <span className="review_section__group_label">{t("review_resolved")}</span>
                            </div>
                            {store.resolvedConflictEntries.map(entry => (
                                <Clickable key={entry.path} onClick={() => store.selectFile(entry.path)}>
                                    <button
                                        className={"review_section__file" + (store.selectedFilePath === entry.path ? " active" : "")}
                                    >
                                        <span className="review_section__file_status review_section__file_status--resolved">
                                            {t("review_resolved_label")}
                                        </span>
                                        <span className="review_section__file_path">{entry.path}</span>
                                    </button>
                                </Clickable>
                            ))}
                        </div>
                    )}

                    {store.stagedEntries.length > 0 && (
                        <div className="review_section__group">
                            <div className="review_section__group_header">
                                <span className="review_section__group_label">{t("review_staged")}</span>
                                <Clickable onClick={() => store.unstageEntries(store.stagedEntries.map(e => e.path))}>
                                    <button className="review_section__group_action" title="Unstage all">−</button>
                                </Clickable>
                            </div>
                            {store.stagedEntries.map(entry => {
                                const label = primaryLabel(entry.labels.filter(l => l.startsWith("index_")));
                                return (
                                <Clickable key={entry.path} onClick={() => store.selectFile(entry.path)}>
                                    <button
                                        className={"review_section__file" + (store.selectedFilePath === entry.path ? " active" : "")}
                                        onContextMenu={(e) => { e.preventDefault(); showFileContextMenu(entry.path, "staged", e.clientX, e.clientY); }}
                                    >
                                        <span className={"review_section__file_status" + (label === "deleted" ? " review_section__file_status--deleted" : " review_section__file_status--staged")}>
                                            {label}
                                        </span>
                                        <span className="review_section__file_path">{entry.path}</span>
                                        <FileActions store={store} path={entry.path} primary="unstage" />
                                    </button>
                                </Clickable>
                                );
                            })}
                        </div>
                    )}

                    {store.unstagedEntries.length > 0 && (
                        <div className="review_section__group">
                            <div className="review_section__group_header">
                                <span className="review_section__group_label">{t("review_unstaged")}</span>
                                <div className="review_section__group_header_actions">
                                    <Clickable onClick={() => store.stageEntries(store.unstagedEntries.map(e => e.path))}>
                                        <button className="review_section__group_action" title="Stage all">+</button>
                                    </Clickable>
                                    <Clickable onClick={() => store.promptRevertGroup(store.unstagedEntries.map(e => e.path))}>
                                        <button className="review_section__group_action" title={t("review_revert_all")}>↶</button>
                                    </Clickable>
                                </div>
                            </div>
                            {store.unstagedEntries.map(entry => {
                                const label = primaryLabel(entry.labels.filter(l => l.startsWith("wt_") && l !== "wt_new"));
                                return (
                                <Clickable key={entry.path} onClick={() => store.selectFile(entry.path)}>
                                    <button
                                        className={"review_section__file" + (store.selectedFilePath === entry.path ? " active" : "")}
                                        onContextMenu={(e) => { e.preventDefault(); showFileContextMenu(entry.path, "unstaged", e.clientX, e.clientY); }}
                                    >
                                        <span className={"review_section__file_status" + (label === "deleted" ? " review_section__file_status--deleted" : " review_section__file_status--unstaged")}>
                                            {label}
                                        </span>
                                        <span className="review_section__file_path">{entry.path}</span>
                                        <FileActions store={store} path={entry.path} primary="stage" />
                                    </button>
                                </Clickable>
                                );
                            })}
                        </div>
                    )}

                    {store.untrackedEntries.length > 0 && (
                        <div className="review_section__group">
                            <div className="review_section__group_header">
                                <span className="review_section__group_label">{t("review_untracked")}</span>
                                <div className="review_section__group_header_actions">
                                    <Clickable onClick={() => store.stageEntries(store.untrackedEntries.map(e => e.path))}>
                                        <button className="review_section__group_action" title="Stage all">+</button>
                                    </Clickable>
                                    <Clickable onClick={() => store.promptRevertGroup(store.untrackedEntries.map(e => e.path))}>
                                        <button className="review_section__group_action" title={t("review_revert_all")}>↶</button>
                                    </Clickable>
                                </div>
                            </div>
                            {store.untrackedEntries.map(entry => (
                                <Clickable key={entry.path} onClick={() => store.selectFile(entry.path)}>
                                    <button
                                        className={"review_section__file" + (store.selectedFilePath === entry.path ? " active" : "")}
                                        onContextMenu={(e) => { e.preventDefault(); showFileContextMenu(entry.path, "untracked", e.clientX, e.clientY); }}
                                    >
                                        <span className="review_section__file_status review_section__file_status--untracked">
                                            {t("review_untracked_short")}
                                        </span>
                                        <span className="review_section__file_path">{entry.path}</span>
                                        <FileActions store={store} path={entry.path} primary="stage" />
                                    </button>
                                </Clickable>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {store.revertAllDialogOpen && (
                <ConfirmDialog
                    title={t("review_revert_all_title")}
                    message={t("review_revert_all_confirm")}
                    confirmLabel={t("review_revert_all_btn")}
                    danger
                    onConfirm={() => store.confirmRevertAll()}
                    onCancel={() => store.cancelRevertAll()}
                />
            )}

            {store.fileToRemove && (
                <ConfirmDialog
                    title={t("review_remove_file_title")}
                    message={t("review_remove_file_confirm").replace("{path}", store.fileToRemove)}
                    confirmLabel={t("review_remove_file_btn")}
                    danger
                    onConfirm={() => store.confirmRemoveFile()}
                    onCancel={() => store.cancelRemoveFile()}
                />
            )}
            {appStore.state.vaultMode && (
                <button className="configure_vault_banner" onClick={() => setShowVaultModal(true)}>
                    {t("vault_configure_banner")}
                </button>
            )}
            {showVaultModal && (
                <ConfigureVaultModal onClose={() => setShowVaultModal(false)} />
            )}
            </>}
        </div>
    );

    const diffContent = store.activeTabPath ? store.diffContents.get(store.activeTabPath) : undefined;
    const hasDiffError = !!store.activeTabPath && store.diffErrors.has(store.activeTabPath);
    const showDiff = !!diffContent && !hasDiffError;

    const diffModifiers = [
        {
            icon: '↵',
            title: wordWrap ? 'Disable word wrap' : 'Enable word wrap',
            active: wordWrap,
            onClick: () => setWordWrap(v => !v),
        },
    ];

    const rightPane = (
        <div className="review_section__pane">
            <Tabs
                leftSlot={!store.isMobile ? <NavigationBar /> : undefined}
                tabs={store.openTabs}
                activeTabPath={store.activeTabPath}
                onTabChange={(path) => store.setActiveTab(path)}
                onTabClose={(path) => store.closeTab(path)}
                onTabCloseAll={() => store.closeAllTabs()}
                onTabCloseOthers={(path) => { store.setActiveTab(path); store.closeAllTabsExceptActive(); }}
                onTabReorder={(from, to) => store.reorderTabs(from, to)}
            >
                {store.syncedFileWithNoChanges && (
                    <div className="review_section__no_selection">
                        {t("review_no_changes_for_file").replace("{path}", store.syncedFileWithNoChanges)}
                    </div>
                )}
                {!store.syncedFileWithNoChanges && !store.activeTabPath && (
                    <div className="review_section__no_selection">{t("review_select_file")}</div>
                )}
                {!store.syncedFileWithNoChanges && hasDiffError && (
                    <div className="review_section__no_selection">{t("review_error")}</div>
                )}
                <Suspense fallback={null}>
                    <MonacoEditor
                        diff={true}
                        original={diffContent?.original ?? ""}
                        modified={store.modifiedValue}
                        diffModifiedEditable={store.activeEditable}
                        onChange={(v: string) => store.onModifiedChange(v)}
                        enableConflictLens={!!store.activeTabPath && store.conflictEntries.some(e => e.path === store.activeTabPath)}
                        getConflicts={() => store.getActiveConflicts()}
                        stashConflict={store.stashConflict}
                        saveNow={() => store.saveActive()}
                        language={diffContent?.language ?? "plaintext"}
                        viewStateKey={store.activeTabPath ?? undefined}
                        scrollSyncKey={store.activeTabPath ?? undefined}
                        scrollSyncSection="review"
                        disableContextMenu={isTouchDevice()}
                        disableSelection={isTouchDevice()}
                        fontSize={store.editorFontSize}
                        onFontSizeChange={(size) => store.setEditorFontSize(size)}
                        hidden={!showDiff || !!store.syncedFileWithNoChanges}
                        modifiers={diffModifiers}
                        wordWrap={wordWrap}
                    />
                </Suspense>
            </Tabs>
            {store.activeEditable && store.isActiveDirty && (
                <div className="review_section__save_overlay">
                    <Clickable onClick={() => !store.isSavingReview && store.saveActive()}>
                        <button
                            className="review_section__save_btn"
                            disabled={store.isSavingReview}
                        >
                            {store.isSavingReview ? <span className="review_section__save_spinner" /> : t("review_save")}
                        </button>
                    </Clickable>
                </div>
            )}
            {store.activeTabPath && (
                <button
                    className={"review_section__context_btn" + (appStore.state.isContextFile(store.activeTabPath) ? " review_section__context_btn--active" : "")}
                    onClick={() => appStore.state.toggleContextFile(store.activeTabPath!)}
                    title={appStore.state.isContextFile(store.activeTabPath) ? "Remove from agent context" : "Add to agent context"}
                >@</button>
            )}
        </div>
    );

    if (store.isMobile) {
        const editToggle = store.activeCanEdit ? (
            <Clickable onClick={() => store.toggleEditMode()}>
                <button
                    className={"review_section__refresh_btn" + (store.activeInEditMode ? " review_section__refresh_btn--active" : "")}
                    title={store.activeInEditMode ? t("review_done_editing") : t("review_edit")}
                >✎</button>
            </Clickable>
        ) : null;

        const diffControls = (
            <>
                {store.activeTabPath && store.mobileAccordionIndex === 1 && (
                    <span className="review_section__active_path">{store.activeTabPath}</span>
                )}
                {editToggle}
            </>
        );

        const mobileItems = [
            { header: t("review_changes"), content: leftPane, headerSlot: !store.isLandscape && store.mobileAccordionIndex === 0 ? branchSelect : null },
            { header: t("code_pane_diff"), content: rightPane, controls: diffControls },
        ];

        return (
            <div className="review_section" data-input-component="reviewsection" data-input-focus="click">
                <Accordion
                    items={mobileItems}
                    openIndex={store.mobileAccordionIndex}
                    onOpenChange={(i: number) => runInAction(() => { store.mobileAccordionIndex = i; })}
                    direction={store.isLandscape ? "horizontal" : "vertical"}
                />
                <ContextMenu store={store.contextMenuStore} options={store.contextMenuStore.options ?? []} />
            </div>
        );
    }

    return (
        <div className="review_section" data-input-component="reviewsection" data-input-focus="click">
            <Split
                split="vertical"
                sizes={store.splitSizes}
                onChange={(sizes: number[]) => store.setSplitSizes(sizes)}
            >
                <SplitPane minSize={160} pinned>{leftPane}</SplitPane>
                <SplitPane minSize={200}>{rightPane}</SplitPane>
            </Split>
            <ContextMenu store={store.contextMenuStore} options={store.contextMenuStore.options ?? []} />
        </div>
    );
}, ReviewSectionStore);
